import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UnitDetailPanel = ({ unit, onClose, onEdit }) => {
  if (!unit) return null;

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US')?.format(mileage);
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200]"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-card border-l border-border shadow-elevation-3 z-[210] overflow-y-auto animate-slide-in">
        <div className="sticky top-0 z-10 bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Truck" size={24} strokeWidth={2} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{unit?.unitNumber}</h2>
                <p className="text-sm text-muted-foreground">{unit?.make} {unit?.model}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-hover"
            >
              <Icon name="X" size={24} strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">VIN</p>
              <p className="text-sm font-medium text-foreground font-mono">{unit?.vin}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Year</p>
              <p className="text-sm font-medium text-foreground">{unit?.year}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Current Mileage</p>
              <p className="text-sm font-medium text-foreground">{formatMileage(unit?.mileage)} miles</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
              <p className="text-sm font-medium text-foreground capitalize">{unit?.status}</p>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">Current Assignment</h3>
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="User" size={18} strokeWidth={2} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{unit?.assignment || 'Unassigned'}</span>
                </div>
                {unit?.assignmentRole && (
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                    {unit?.assignmentRole}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={18} strokeWidth={2} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{unit?.yard}</span>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">Assignment History</h3>
            <div className="space-y-3">
              {unit?.assignmentHistory?.map((history, index) => (
                <div key={index} className="flex items-start space-x-3 pb-3 border-b border-border last:border-0">
                  <div className="flex items-center justify-center w-8 h-8 bg-muted rounded-full flex-shrink-0 mt-1">
                    <Icon name="Clock" size={16} strokeWidth={2} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground">{history?.driver}</p>
                      <span className="text-xs text-muted-foreground">{history?.role}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(history?.startDate)} - {history?.endDate ? formatDate(history?.endDate) : 'Present'}
                    </p>
                    <p className="text-xs text-muted-foreground">{history?.yard}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">Maintenance Records</h3>
            <div className="space-y-3">
              {unit?.maintenanceRecords?.map((record, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{record?.type}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(record?.date)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{record?.description}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>Mileage: {formatMileage(record?.mileage)}</span>
                    <span>Cost: ${record?.cost?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">Integration Status</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 text-success rounded-lg">
                <Icon name="CheckCircle" size={16} strokeWidth={2} />
                <span className="text-xs font-medium">GPS Active</span>
              </div>
              <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 text-success rounded-lg">
                <Icon name="CheckCircle" size={16} strokeWidth={2} />
                <span className="text-xs font-medium">Insurance</span>
              </div>
              <div className="flex items-center space-x-2 px-3 py-2 bg-warning/10 text-warning rounded-lg">
                <Icon name="AlertCircle" size={16} strokeWidth={2} />
                <span className="text-xs font-medium">Maintenance</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3 pt-4">
            <Button
              variant="default"
              fullWidth
              iconName="Edit"
              iconPosition="left"
              onClick={() => onEdit(unit)}
            >
              Edit Unit
            </Button>
            <Button
              variant="outline"
              fullWidth
              iconName="Download"
              iconPosition="left"
            >
              Export Report
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnitDetailPanel;